import React, { useEffect, useState } from 'react'
import { ScrollView, StyleSheet, Text, View } from 'react-native'
import { COLORS, COMMON_STYLES, Spinner, TEXT_STYLES, TYPOGRAPHY } from '@dehaat/dds'
import fetchDemoMaterialRequestDetail from 'app/apis/fetchDemoMaterialRequestDetail'
import { DemoMaterialRequest } from 'app/models/RequestFlow'
import { DemoRequestDetailScreenProps } from 'app/routes/types'

const DemoRequestDetail = ({ route }: DemoRequestDetailScreenProps) => {
  const { requestId } = route.params
  const [data, setData] = useState<DemoMaterialRequest | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getDetails = async () => {
      try {
        const response = await fetchDemoMaterialRequestDetail(requestId)
        setData(response)
      } finally {
        setLoading(false)
      }
    }
    getDetails()
  }, [requestId])

  if (loading) return <Spinner />
  if (!data) return <Text style={styles.empty}>Unable to load request detail</Text>

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.title}>Request #{data.id}</Text>
      <Text style={styles.status}>Status: {data.latest_status}</Text>
      <Detail label="Vendor" value={data.vendor?.company_name || '-'} />
      <Detail
        label="Product"
        value={`${data.product_variant?.product_name || '-'} ${data.product_variant?.attributes_name || ''}`}
      />
      <Detail label="Farmer Name" value={data.farmer_name} />
      <Detail label="Farmer Mobile" value={data.farmer_mobile} />
      <Detail label="Units" value={String(data.units)} />
      <Detail label="Address" value={data.address} />
      <Detail label="PIN" value={data.pin_code} />
      <Detail label="State" value={data.state?.name || '-'} />
      <Detail label="District" value={data.district?.name || '-'} />
      <Detail label="Block" value={data.block?.name || '-'} />
      <Detail label="Remarks" value={data.remarks || '-'} />
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Approvers</Text>
        {data.approvers?.map((approver, idx) => (
          <Text style={styles.approver} key={`${approver.level}_${idx}`}>
            {approver.level}: {approver.status} {approver.approver_name ? `(${approver.approver_name})` : ''}
          </Text>
        ))}
      </View>
    </ScrollView>
  )
}

const Detail = ({ label, value }: { label: string; value: string }) => (
  <View style={styles.row}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
  </View>
)

const styles = StyleSheet.create({
  container: { padding: 12, rowGap: 8 },
  title: { ...TYPOGRAPHY.HEADING3, ...TEXT_STYLES.textNeutral90 },
  status: { ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textNeutral70, marginBottom: 6 },
  row: {
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    borderRadius: 8,
    padding: 10,
    backgroundColor: COLORS.white,
  },
  label: { ...TYPOGRAPHY.CAPTION1, ...TEXT_STYLES.textNeutral70 },
  value: { ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textNeutral90 },
  section: {
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    borderRadius: 8,
    padding: 10,
    backgroundColor: COLORS.white,
  },
  sectionTitle: { ...TYPOGRAPHY.TEXT1, ...TEXT_STYLES.textNeutral90, marginBottom: 6 },
  approver: { ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textNeutral80, marginBottom: 4 },
  empty: { padding: 16, ...TYPOGRAPHY.TEXT2, ...TEXT_STYLES.textNeutral70 },
})

export default DemoRequestDetail
